import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef
} from 'react';
import { CSSTransition } from 'react-transition-group';
import tailwindColors from 'tailwindcss/colors';
import {
	differenceInBusinessDays,
	differenceInDays,
	eachDayOfInterval,
	format,
	parse
} from 'date-fns';
import {
	Area,
	AreaChart,
	CartesianGrid,
	Pie,
	PieChart,
	XAxis,
	YAxis
} from 'recharts';

import { EmptyMessage } from '@/components/atoms/EmptyMessage';

import { sendAnalytics } from '@/services/analyticsService';
import { Chart, ChartConfig } from '@/components/atoms/Charts';
import {
	DAILY_AVERAGE_CONTACT,
	ReportCategoryResult,
	ReportUserPeriod
} from '../../@types/types';
import { useReportData } from '../../hooks/useReportData';
import { useCategoriesData } from '../../hooks/useCategoriesData';
import { handleExportAsCsv } from '../../utils/exportAsCsv';
import { handleExportAsPng } from '../../utils/exportAsPng';
import { useUserData } from '../../hooks/useUserData';
import { useReportsQuery } from '../../hooks/useReportsQuery';
import { useSubordinatedListsData } from '../../hooks/useSubordinatedListsData';

type ChartsProps = {
	reportUserPeriod: ReportUserPeriod;
	onUpdateChartsExistence: (value: boolean) => void;
};

export type ChartsHandle = {
	onExportAsCsv: () => void;
	onExportAsPng: () => void;
};

export const Charts = memo(
	forwardRef<ChartsHandle, ChartsProps>(
		({ reportUserPeriod, onUpdateChartsExistence }, ref): JSX.Element => {
			const chartsRef = useRef<HTMLDivElement>(null);

			const { report, reportIsLoading } = useReportData();
			const { categories } = useCategoriesData();
			const { user } = useUserData();
			const { lists = [] } = useSubordinatedListsData();

			const { getReports } = useReportsQuery();

			useEffect(() => {
				onUpdateChartsExistence(
					Boolean(chartsRef.current && report.totalContacted)
				);
			}, [onUpdateChartsExistence, report.totalContacted]);

			useEffect(() => {
				getReports({
					...reportUserPeriod
				});

				if (user?.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User viewed a report',
						properties: {
							startDate: reportUserPeriod?.startDate,
							endDate: reportUserPeriod?.endDate,
							userEmail: reportUserPeriod?.userEmail,
							listId: reportUserPeriod?.listId,
							listName: reportUserPeriod?.listName,
							method: reportUserPeriod?.method
						}
					});
				}
			}, [getReports, reportUserPeriod, user.email]);

			const categoryReport = useMemo(() => {
				if (!report)
					return {
						data: [],
						config: {}
					};

				const reducedCategories = Object.entries(report).reduce<
					{
						name: string;
						value: number;
						color: string;
						fill: string;
					}[]
				>((acc, [key, value]) => {
					const category = categories?.find(
						c =>
							String(c.name).toLowerCase().trim() ===
							String(key).toLowerCase().trim()
					);

					if (category) {
						acc.push({
							name: key,
							value:
								(value as ReportCategoryResult).contacted ?? 0,
							color: category.color ?? '',
							fill: `var(--color-${key.toLowerCase().replace(/ /g, '-')})`
						});
					}

					return acc;
				}, []);

				const sortedCategories = reducedCategories.sort((a, b) => {
					if (a.value > b.value) {
						return -1;
					}

					if (a.value < b.value) {
						return 1;
					}

					return 0;
				});

				const chartConfig: ChartConfig = Object.assign(
					{},
					...sortedCategories.map(sortedReport => ({
						[sortedReport.name.toLowerCase().replace(/ /g, '-')]: {
							label: sortedReport.name,
							color: sortedReport.color
						}
					}))
				);

				return { data: sortedCategories, config: chartConfig };
			}, [categories, report]);

			const contactedReport = useMemo(() => {
				if (!report.result)
					return {
						data: [],
						config: {}
					};

				const sortedReports = Object.entries(report.result)
					.map(([key, value]) => ({
						dateTime: parse(key, 'yyyy-M-d', new Date()).getTime(),
						name: format(
							parse(key, 'yyyy-M-d', new Date()).getTime(),
							'LLL, d'
						),
						value: value.totalContacted || 0
					}))
					.sort((a, b) => b.dateTime - a.dateTime);

				const chartConfig: ChartConfig = Object.assign(
					{},
					...sortedReports.map(sortedReport => ({
						value: {
							label: sortedReport.name
						}
					}))
				);

				return { data: sortedReports, config: chartConfig };
			}, [report.result]);

			const onExportAsCsv = useCallback(() => {
				handleExportAsCsv({
					report,
					reportUserPeriod,
					categoryReport: categoryReport.data,
					user,
					lists
				});
			}, [categoryReport.data, lists, report, reportUserPeriod, user]);

			const onExportAsPng = useCallback(() => {
				handleExportAsPng({
					container: chartsRef.current,
					reportUserPeriod,
					user
				});
			}, [reportUserPeriod, user]);

			useImperativeHandle(
				ref,
				() => ({
					onExportAsCsv,
					onExportAsPng
				}),
				[onExportAsCsv, onExportAsPng]
			);

			const userAveragePercentMod = useMemo(() => {
				const diffInDays =
					differenceInBusinessDays(
						reportUserPeriod.endDate,
						reportUserPeriod.startDate
					) + 1;

				return (
					100 -
					Math.trunc(
						((report.totalContacted ?? 0) * 100) /
							(DAILY_AVERAGE_CONTACT * diffInDays)
					)
				);
			}, [
				report.totalContacted,
				reportUserPeriod.endDate,
				reportUserPeriod.startDate
			]);

			return report.totalContacted ? (
				<CSSTransition
					in={!reportIsLoading}
					timeout={500}
					classNames="fade"
				>
					<div className="flex h-full flex-col gap-4 overflow-hidden">
						<header className="flex flex-col gap-3 p-5">
							<h2 className="text-4xl font-black text-dm-white">
								{report.totalContacted}
							</h2>

							<p className="text-gray-300">
								{reportUserPeriod.listId ? (
									<p>
										<span className="text-light-green">
											{report.listBusinessesAmount
												? Math.trunc(
														((report.totalContacted ??
															0) *
															100) /
															report.listBusinessesAmount
													)
												: 0}
											%
										</span>{' '}
										of businesses in list
									</p>
								) : (
									<p>
										<span
											data-user-avg-percentage-higher-than-zero={
												userAveragePercentMod < 0
											}
											className="text-light-red data-[user-avg-percentage-higher-than-zero=true]:text-light-green"
										>
											{Math.abs(userAveragePercentMod)}%{' '}
											{userAveragePercentMod <= 0
												? 'up'
												: 'down'}
										</span>{' '}
										from user average
									</p>
								)}
							</p>
						</header>

						<section
							ref={chartsRef}
							className="flex flex-1 flex-col justify-center gap-4 overflow-auto p-3 lg:flex-row lg:overflow-hidden"
						>
							<div className="flex h-full w-full flex-col justify-end gap-4 lg:max-w-[50%] lg:gap-6">
								<Chart.Container
									config={contactedReport.config}
								>
									<AreaChart
										data={contactedReport.data}
										margin={{
											left: 12,
											right: 12
										}}
									>
										<CartesianGrid
											vertical={false}
											stroke="#424242"
										/>

										<XAxis
											type="number"
											dataKey="dateTime"
											domain={[
												reportUserPeriod.startDate.getTime(),
												reportUserPeriod.endDate.getTime()
											]}
											tick={
												<ChartTick
													position="x"
													reportUserPeriod={
														reportUserPeriod
													}
												/>
											}
											interval={(() => {
												const diff = differenceInDays(
													reportUserPeriod.endDate,
													reportUserPeriod.startDate
												);

												if (diff >= 365) {
													return 60;
												}

												if (diff > 60) {
													return 30;
												}

												if (diff >= 14) {
													return 6;
												}

												if (diff < 14 && diff > 7) {
													return 1;
												}

												return 0;
											})()}
											ticks={eachDayOfInterval({
												start: reportUserPeriod.startDate,
												end: reportUserPeriod.endDate
											}).map(value => value.getTime())}
											allowDataOverflow
											padding={{
												left: 20
											}}
										/>

										<YAxis
											type="number"
											tick={
												<ChartTick
													position="y"
													reportUserPeriod={
														reportUserPeriod
													}
												/>
											}
											tickCount={40}
											interval={9}
											tickLine={false}
											allowDecimals={false}
											allowDataOverflow
										/>

										<defs>
											<linearGradient
												id="colorUv"
												x1="0"
												y1="0"
												x2="0"
												y2="1"
											>
												<stop
													offset="0%"
													stopColor="#ACB9FF"
													stopOpacity={0.8}
												/>
												<stop
													offset="100%"
													stopColor="#ACB9FF"
													stopOpacity={0}
												/>
											</linearGradient>
										</defs>

										<Chart.Tooltip
											cursor={{
												stroke: '#ACB9FF',
												strokeWidth: 1,
												strokeDasharray: '3 3'
											}}
											content={
												<Chart.TooltipContent
													formatter={(
														value,
														name,
														props
													) =>
														`${props.payload.name} - ${value}`
													}
												/>
											}
										/>

										<Area
											dataKey="value"
											type="linear"
											fillOpacity={0.4}
											stroke="var(--color-value)"
											fill="url(#colorUv)"
											activeDot={{
												stroke: 'white',
												fill: 'white',
												strokeWidth: 1
											}}
										/>
									</AreaChart>
								</Chart.Container>

								<p className="text-center text-xs font-semibold text-dm-white md:text-sm lg:text-base">
									Number of businesses
								</p>
							</div>

							<div className="flex h-full w-full flex-col justify-end gap-4 lg:max-w-[50%] lg:gap-6">
								<Chart.Container config={categoryReport.config}>
									<PieChart>
										<Chart.Tooltip
											content={<Chart.TooltipContent />}
										/>

										<Pie
											data={categoryReport.data}
											label={ChartPieLabel}
											dataKey="value"
											nameKey="name"
											labelLine={false}
											isAnimationActive={false}
										/>
									</PieChart>
								</Chart.Container>

								<p className="text-center text-xs font-semibold text-dm-white md:text-sm lg:text-base">
									Types of businesses
								</p>
							</div>
						</section>
					</div>
				</CSSTransition>
			) : (
				<EmptyMessage
					title="No activity to report yet!"
					message="Easily reach out to businesses by email, Facebook, and phone calls all through Resquared."
					className="relative h-full"
				/>
			);
		}
	)
);

type TickProps = Pick<ChartsProps, 'reportUserPeriod'> & {
	x?: number;
	y?: number;
	position: string;
	payload?: {
		value: number | undefined;
	};
};

const ChartTick = memo(
	({ x, y, payload, position, reportUserPeriod }: TickProps) => {
		const days = eachDayOfInterval({
			start: reportUserPeriod.startDate,
			end: reportUserPeriod.endDate
		});

		const formattedDate = (): string => {
			if (days.length <= 7) {
				return format(payload?.value as number, 'eee');
			}

			if (days.length > 7 && days.length <= 21) {
				return format(payload?.value as number, 'eee do');
			}

			return `${format(payload?.value as number, 'LLL d')}`;
		};

		const formattedValue = () => {
			const val = Number(payload?.value);

			if (val % DAILY_AVERAGE_CONTACT === 0) {
				return val;
			}

			return '';
		};

		return (
			<text
				x={x}
				y={y}
				fill="#E2E0DF"
				fontSize={12}
				dx={position === 'x' ? 0 : -10}
				dy={position === 'y' ? 0 : 10}
				textAnchor="middle"
			>
				{position === 'y' ? formattedValue() : formattedDate()}
			</text>
		);
	}
);

const ChartPieLabel = ({
	cx,
	cy,
	fill,
	midAngle,
	outerRadius,
	percent,
	name
}: {
	cx: number;
	cy: number;
	fill: string;
	midAngle: number;
	outerRadius: number;
	percent: number;
	name: string;
}) => {
	const percentCalculated = Number(percent) * 100;

	if (Number(percentCalculated) <= 10) {
		return null;
	}

	const RADIAN = Math.PI / 180;

	const sin = Math.sin(-RADIAN * (midAngle ?? 0));
	const cos = Math.cos(-RADIAN * (midAngle ?? 0));

	const sx = Number(cx) + (Number(outerRadius) + 5) * cos;
	const sy = Number(cy) + (Number(outerRadius) + 5) * sin;

	const mx = Number(cx) + Number(outerRadius) * cos;
	const my = Number(cy) + (Number(outerRadius) + 10) * sin;

	const ex = mx + (cos >= 0 ? 1 : -1) * 20;

	const ey = my;

	const textAnchor = cos >= 0 ? 'start' : 'end';

	return (
		<g className="relative z-50">
			<path
				d={`M${sx},${sy}L${ex},${ey}`}
				stroke={tailwindColors.gray[200]}
				fill="none"
			/>
			<circle
				cx={ex}
				cy={ey}
				r={2}
				fill={tailwindColors.gray[200]}
				stroke="none"
			/>
			<text
				x={ex + (cos >= 0 ? 1 : -1) * 10}
				y={ey}
				fill={fill}
				textAnchor={textAnchor}
				fontWeight="bold"
				fontSize={13}
			>
				{name}
			</text>
			<text
				x={ex + (cos >= 0 ? 1 : -1) * 10}
				y={ey}
				dy={15}
				textAnchor={textAnchor}
				fill="#E2E0DF"
				fontSize={12}
			>
				{Math.trunc(percentCalculated)}%
			</text>
		</g>
	);
};
